import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { PeriodMonthColumn } from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager-month';
import type { Period } from 'lumen/models';
import { formatOverbookingValue } from 'lumen/utils/pricing-assistant/restriction-display-utils';

interface HotelPricingAssistantTableCellsTdOverbookingMonthSignature {
  Args: {
    column: PeriodMonthColumn;
    value: Period;
  };
}

export default class HotelPricingAssistantTableCellsTdOverbookingMonth extends Component<HotelPricingAssistantTableCellsTdOverbookingMonthSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service('pricing-assistant-data-request-service')
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  get displayValue(): number | string {
    return formatOverbookingValue({ intl: this.intl, value: this.overbooking });
  }

  /* The overbooking needs both restrictions requests, as the Rate groups cell does.
     Rooms have no overbooking pushes yet. */
  get isLoading(): boolean {
    const { restrictionsRecommendationsRequestTask, restrictionsRequestTask } =
      this.pricingAssistantDataRequestService;

    return (
      restrictionsRequestTask.isRunning ||
      restrictionsRecommendationsRequestTask.isRunning ||
      (!this.isRoom && this.args.value.hasOverbookingActionInProgress)
    );
  }

  get isMissing(): boolean {
    return this.overbooking == null;
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.value.fromDate);
  }

  get isRoom(): boolean {
    return this.args.column.pmsRoomId !== undefined;
  }

  get overbooking(): number | null {
    const { pmsRoomId } = this.args.column;

    return pmsRoomId !== undefined
      ? this.args.value.roomOverbooking(pmsRoomId)
      : this.args.value.effectiveOverbookingWithOverride;
  }

  <template>
    {{#if this.isLoading}}
      <div class="loading-pulse" data-test-id="loading-pulse"></div>
    {{else}}
      <div
        class="mr-500
          {{if
            this.isPast
            'text-disabled'
            (if this.isMissing 'text-neutral-subdued')
          }}"
        data-test-id={{if
          this.isRoom
          "td-room-overbooking-month"
          "td-hotel-overbooking-month"
        }}
      >
        {{this.displayValue}}
      </div>
    {{/if}}
  </template>
}
